import { useCallback, useEffect, useRef } from 'react';
import { notify, shouldEmitSystemNotification } from '../utils/notifications.js';
import { clearBrowserAttention, requestBrowserAttention } from '../utils/browserAttention.js';
import { showToast } from '../utils/toast';

const TAB_ID_STORAGE_KEY = 'bluezap:tab-id';
const ACTIVE_TAB_STORAGE_KEY = 'bluezap:active-tab';
const ACTIVE_TAB_CHANNEL = 'bluezap:active-tab-channel';
const ACTIVE_TAB_HEARTBEAT_MS = 4000;
const ACTIVE_TAB_TTL_MS = 10000;
const VISIBLE_NOTIFICATION_BATCH_WINDOW_MS = 900;
const VISIBLE_NOTIFICATION_BATCH_MAX_ITEMS = 4;

const createTabId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getOrCreateTabId = () => {
  if (typeof window === 'undefined') return createTabId();
  try {
    const stored = window.sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (stored) return stored;
    const id = createTabId();
    window.sessionStorage.setItem(TAB_ID_STORAGE_KEY, id);
    return id;
  } catch {
    return createTabId();
  }
};

const readActiveTab = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const id = parsed.id !== undefined && parsed.id !== null ? String(parsed.id) : '';
    const ts = Number(parsed.ts);
    if (!id || Number.isNaN(ts)) return null;
    return { id, ts };
  } catch {
    return null;
  }
};

const writeActiveTab = (id, ts = Date.now()) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      ACTIVE_TAB_STORAGE_KEY,
      JSON.stringify({ id, ts })
    );
  } catch {
    // ignore storage failures
  }
};

const clearActiveTab = (expectedId) => {
  if (typeof window === 'undefined') return false;
  if (!expectedId) return false;
  try {
    const current = readActiveTab();
    if (current?.id !== expectedId) return false;
    window.localStorage.removeItem(ACTIVE_TAB_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
};

const isActiveTabFresh = (entry) => {
  if (!entry) return false;
  const age = Date.now() - entry.ts;
  return age >= 0 && age <= ACTIVE_TAB_TTL_MS;
};

const pickMostRecent = (a, b) => {
  if (!a) return b || null;
  if (!b) return a || null;
  return a.ts >= b.ts ? a : b;
};

const isNotificationDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('notification_debug') === 'true';
  } catch {
    return false;
  }
};

const debugNotificationFlow = (event, details = {}) => {
  if (!isNotificationDebugEnabled()) return;
  console.debug('[notification-flow]', event, details);
};

const readDocumentVisibilityState = () => {
  if (typeof document === 'undefined') return 'unknown';
  return typeof document.visibilityState === 'string'
    ? document.visibilityState
    : 'unknown';
};

const readDocumentHasFocus = () => {
  if (typeof document === 'undefined') return false;
  if (typeof document.hasFocus !== 'function') return false;
  try {
    return document.hasFocus();
  } catch {
    return false;
  }
};

const serializeActiveTabForDebug = (entry) => {
  if (!entry) return null;
  return {
    id: entry.id || null,
    ts: Number.isFinite(Number(entry.ts)) ? Number(entry.ts) : null,
  };
};

const shouldBatchVisibleNotification = (attention) => {
  const kind = typeof attention?.kind === 'string' ? attention.kind.trim().toLowerCase() : '';
  return kind === 'message' || kind === 'new-client';
};

const formatVisibleBatchLine = ({ title, body }) => {
  const titleText = typeof title === 'string' ? title.trim() : '';
  const bodyText = typeof body === 'string' ? body.trim() : '';
  const firstBodyLine = bodyText
    .split('\n')
    .map((line) => line.trim())
    .find(Boolean);
  if (titleText && firstBodyLine) return `${titleText} - ${firstBodyLine}`;
  return titleText || firstBodyLine || '';
};

export function useNotificationGate() {
  const tabIdRef = useRef(null);
  const activeTabChannelRef = useRef(null);
  const activeTabMemoryRef = useRef(null);
  const visibleToastBatchRef = useRef([]);
  const visibleToastBatchTimerRef = useRef(null);

  if (tabIdRef.current === null) {
    tabIdRef.current = getOrCreateTabId();
  }

  const isPageVisible = useCallback(() => {
    if (typeof document === 'undefined') return false;
    const visibilityOk =
      typeof document.visibilityState === 'string' ? document.visibilityState === 'visible' : true;
    const focusOk =
      typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    return visibilityOk && focusOk;
  }, []);

  const rememberActiveTab = useCallback((entry) => {
    if (!entry) return;
    const current = activeTabMemoryRef.current;
    if (!current || entry.ts >= current.ts) {
      activeTabMemoryRef.current = entry;
    }
  }, []);

  const publishActiveTab = useCallback(
    (tabId) => {
      if (!tabId) return null;
      const ts = Date.now();
      const entry = { id: tabId, ts };
      writeActiveTab(tabId, ts);
      rememberActiveTab(entry);
      const channel = activeTabChannelRef.current;
      if (channel) {
        try {
          channel.postMessage({ type: 'active', id: tabId, ts });
        } catch {
          // ignore broadcast failures
        }
      }
      return ts;
    },
    [rememberActiveTab]
  );

  const clearActiveTabIfCurrent = useCallback((tabId) => {
    if (!tabId) return;
    const cleared = clearActiveTab(tabId);
    if (activeTabMemoryRef.current?.id === tabId) {
      activeTabMemoryRef.current = null;
    }
    if (!cleared) return;
    const channel = activeTabChannelRef.current;
    if (channel) {
      try {
        channel.postMessage({ type: 'inactive', id: tabId, ts: Date.now() });
      } catch {
        // ignore broadcast failures
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const tabId = tabIdRef.current;
    if (!tabId) {
      return undefined;
    }

    const channel =
      typeof BroadcastChannel === 'function'
        ? new BroadcastChannel(ACTIVE_TAB_CHANNEL)
        : null;
    activeTabChannelRef.current = channel;

    let heartbeatId = null;

    const handleChannelMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;
      const type = typeof data.type === 'string' ? data.type : '';
      const id = data.id !== undefined && data.id !== null ? String(data.id) : '';
      if (!type || !id) return;

      if (type === 'active') {
        const ts = Number(data.ts);
        if (Number.isNaN(ts)) return;
        const entry = { id, ts };
        rememberActiveTab(entry);
        const current = readActiveTab();
        const next = pickMostRecent(current, entry);
        if (!current || (next && (next.id !== current.id || next.ts !== current.ts))) {
          writeActiveTab(next.id, next.ts);
        }
        return;
      }

      if (type === 'inactive') {
        const current = readActiveTab();
        if (current?.id === id) {
          clearActiveTab(id);
        }
        if (activeTabMemoryRef.current?.id === id) {
          activeTabMemoryRef.current = null;
        }
      }
    };

    const syncActiveTab = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        clearBrowserAttention();
      }
      if (isPageVisible()) {
        publishActiveTab(tabId);
        if (heartbeatId === null) {
          heartbeatId = window.setInterval(() => {
            if (isPageVisible()) {
              publishActiveTab(tabId);
            }
          }, ACTIVE_TAB_HEARTBEAT_MS);
        }
      } else if (heartbeatId !== null) {
        window.clearInterval(heartbeatId);
        heartbeatId = null;
      }
    };

    const handlePageHide = () => {
      clearActiveTabIfCurrent(tabId);
    };

    syncActiveTab();

    if (channel) {
      channel.addEventListener('message', handleChannelMessage);
    }
    window.addEventListener('focus', syncActiveTab);
    window.addEventListener('blur', syncActiveTab);
    document.addEventListener('visibilitychange', syncActiveTab);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      clearBrowserAttention();
      if (heartbeatId !== null) {
        window.clearInterval(heartbeatId);
      }
      if (channel) {
        channel.removeEventListener('message', handleChannelMessage);
        channel.close();
      }
      if (activeTabChannelRef.current === channel) {
        activeTabChannelRef.current = null;
      }
      window.removeEventListener('focus', syncActiveTab);
      window.removeEventListener('blur', syncActiveTab);
      document.removeEventListener('visibilitychange', syncActiveTab);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, [isPageVisible, publishActiveTab, clearActiveTabIfCurrent, rememberActiveTab]);

  const flushVisibleToastBatch = useCallback(() => {
    const batch = visibleToastBatchRef.current;
    visibleToastBatchRef.current = [];
    visibleToastBatchTimerRef.current = null;
    if (!batch.length) return;

    if (batch.length === 1) {
      const [notification] = batch;
      showToast('', {
        title: notification.title,
        description: notification.body,
        iconUrl: notification.icon,
        onClick: notification.onClick,
      });
      return;
    }

    const visibleLines = batch
      .slice(0, VISIBLE_NOTIFICATION_BATCH_MAX_ITEMS)
      .map(formatVisibleBatchLine)
      .filter(Boolean);
    const remainingCount = batch.length - visibleLines.length;
    if (remainingCount > 0) {
      visibleLines.push(`+${remainingCount} outras notificações`);
    }

    const latestNotification = batch[batch.length - 1] || {};
    showToast('', {
      title: `${batch.length} novas notificações`,
      description: visibleLines.join('\n'),
      iconUrl: latestNotification.icon || '/logo.png',
      timeout: Math.min(7000, 3500 + batch.length * 500),
      onClick: latestNotification.onClick,
    });
  }, []);

  const scheduleVisibleToastBatch = useCallback(
    (notification) => {
      if (typeof window === 'undefined') return;
      visibleToastBatchRef.current.push(notification);
      if (visibleToastBatchTimerRef.current !== null) return;
      visibleToastBatchTimerRef.current = window.setTimeout(
        flushVisibleToastBatch,
        VISIBLE_NOTIFICATION_BATCH_WINDOW_MS
      );
    },
    [flushVisibleToastBatch]
  );

  useEffect(
    () => () => {
      if (visibleToastBatchTimerRef.current !== null) {
        window.clearTimeout(visibleToastBatchTimerRef.current);
        visibleToastBatchTimerRef.current = null;
      }
      visibleToastBatchRef.current = [];
    },
    []
  );

  const emitPopupNotification = useCallback(
    (
      title,
      {
        body,
        icon,
        tag,
        onClick,
        attention,
        debugMeta,
        systemWhenVisible = false,
      } = {}
    ) => {
      const invokeClick = (source) => {
        if (typeof onClick === 'function') {
          onClick({ source });
        }
      };
      const visible = isPageVisible();
      const storedActive = readActiveTab();
      const memoryActive = activeTabMemoryRef.current;
      const activeTab = pickMostRecent(storedActive, memoryActive);
      const now = Date.now();
      const activeTabHeartbeatAgeMs =
        activeTab && Number.isFinite(Number(activeTab.ts))
          ? now - Number(activeTab.ts)
          : null;
      const hasFreshActiveTab = isActiveTabFresh(activeTab);
      const tabId = tabIdRef.current;
      const hasOtherActiveTab =
        hasFreshActiveTab && activeTab?.id && tabId && activeTab.id !== tabId;
      const shouldEmitSystem = shouldEmitSystemNotification({
        pageVisible: visible,
        systemWhenVisible,
        hasOtherActiveTab,
      });
      const notificationPermission =
        typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
      const notificationDebugMeta = {
        conversation_id: debugMeta?.conversationId ?? debugMeta?.conversation_id ?? debugMeta?.chatId ?? debugMeta?.chat_id ?? null,
        chat_id: debugMeta?.chatId ?? debugMeta?.chat_id ?? null,
        message_key: debugMeta?.messageKey ?? debugMeta?.message_key ?? null,
        notification_kind: debugMeta?.kind ?? attention?.kind ?? null,
        tag: tag || null,
      };

      debugNotificationFlow('gate_received', {
        ...notificationDebugMeta,
        document_visibilityState: readDocumentVisibilityState(),
        document_hasFocus: readDocumentHasFocus(),
        pageVisible: visible,
        tabId,
        storedActiveTab: serializeActiveTabForDebug(storedActive),
        activeTab: serializeActiveTabForDebug(activeTab),
        heartbeat_age_ms: activeTabHeartbeatAgeMs,
        hasOtherActiveTab: Boolean(hasOtherActiveTab),
        shouldEmitSystemNotification: shouldEmitSystem,
        Notification_permission: notificationPermission,
        systemWhenVisible: Boolean(systemWhenVisible),
      });

      if (visible) {
        debugNotificationFlow('page_visible_toast', {
          ...notificationDebugMeta,
          systemWhenVisible: Boolean(systemWhenVisible),
        });
        if (shouldBatchVisibleNotification(attention)) {
          scheduleVisibleToastBatch({
            title,
            body,
            icon,
            onClick: () => invokeClick('toast'),
          });
        } else {
          showToast('', {
            title,
            description: body,
            iconUrl: icon,
            onClick: () => invokeClick('toast'),
          });
        }

        if (!systemWhenVisible) return;
      }

      debugNotificationFlow(
        typeof document !== 'undefined' && document.visibilityState !== 'visible'
          ? 'document_hidden'
          : 'document_blurred',
        {
          ...notificationDebugMeta,
          Notification_permission: notificationPermission,
          hasOtherActiveTab: Boolean(hasOtherActiveTab),
          shouldEmitSystemNotification: shouldEmitSystem,
        }
      );
      debugNotificationFlow('gate_decision', {
        ...notificationDebugMeta,
        emit_system: Boolean(shouldEmitSystem),
        suppress_reason: shouldEmitSystem
          ? null
          : hasOtherActiveTab
            ? 'other_active_tab'
            : 'page_visible_without_systemWhenVisible',
        shouldEmitSystemNotification: shouldEmitSystem,
        hasOtherActiveTab: Boolean(hasOtherActiveTab),
        Notification_permission: notificationPermission,
      });

      if (shouldEmitSystem) {
        if (
          attention?.enabled &&
          typeof document !== 'undefined' &&
          document.visibilityState !== 'visible'
        ) {
          requestBrowserAttention({
            title: attention.title || title,
            kind: attention.kind || 'message',
          });
        }
        if (typeof Notification === 'undefined') {
          debugNotificationFlow('notification_unsupported', notificationDebugMeta);
        } else if (Notification.permission !== 'granted') {
          debugNotificationFlow('notification_permission_denied', {
            ...notificationDebugMeta,
            permission: Notification.permission,
          });
        }
        notify(title, {
          body,
          icon,
          tag,
          autoCloseAfterMs: 4000,
          debugMeta: notificationDebugMeta,
          onClick: () => invokeClick('system'),
        });
      } else if (hasOtherActiveTab) {
        debugNotificationFlow('active_tab_suppressed_system_notification', notificationDebugMeta);
      } else {
        debugNotificationFlow('visible_tab_suppressed_system_notification', notificationDebugMeta);
      }
    },
    [isPageVisible, scheduleVisibleToastBatch]
  );

  return { emitPopupNotification };
}
